/**
 * Client-side mirror of the server's push-channel contracts (task 20.3).
 *
 * These types intentionally track the shapes the server fans out over the
 * WebSocket (Design: "results are always delivered over the WebSocket push
 * channel"). The authoritative definitions live server-side:
 *
 *   - `FREE_FLOW_MESSAGE_PUSH_TYPE` / free-flow message body —
 *     packages/lambda/src/interaction-modes/free-flow-channel.ts
 *   - `WRITING_INDICATOR_PUSH_TYPE` / writing-indicator body —
 *     packages/lambda/src/interaction-modes/staleness.ts
 *   - `TurnResultPush` (kind:"result") / `TurnRetryPush` (kind:"retry") —
 *     packages/lambda/src/turn-pipeline/commit.ts
 *   - the generic `PushMessage` envelope —
 *     packages/lambda/src/websocket/push-channel.ts
 *
 * We keep the string constants in one place so a server contract change is a
 * single-line edit here, and the union stays exhaustive.
 */

import type { MessageMode } from "@rufus/shared-types";

/** Mirrors FREE_FLOW_MESSAGE_PUSH_TYPE (free-flow-channel.ts). */
export const FREE_FLOW_MESSAGE_PUSH_TYPE = "free_flow_message";
/** Mirrors WRITING_INDICATOR_PUSH_TYPE (staleness.ts). */
export const WRITING_INDICATOR_PUSH_TYPE = "writing_indicator";
/** Mirrors COLD_START_WAITING_PUSH_TYPE (staleness.ts) — the cold-start waiting cue (R15.2). */
export const COLD_START_WAITING_PUSH_TYPE = "cold_start_waiting";
/** The DM's asynchronous interjection push (see push-channel.test.ts). */
export const DM_INTERJECTION_PUSH_TYPE = "dm_interjection";
/** Mirrors RECAP_PUSH_TYPE (lambda memory/recap.ts) — the session "previously…" recap. */
export const RECAP_PUSH_TYPE = "session_recap";
/** Mirrors SYSTEM_AUDIT_PUSH_TYPE (lambda write-path/repository-store.ts) — a committed ledger/campaign audit line. */
export const SYSTEM_AUDIT_PUSH_TYPE = "system_audit";
/**
 * Mirrors ROLL_REQUEST_PUSH_TYPE (lambda; multi-leg turn model A3) — a request
 * for a player to roll an owed die for a PAUSED turn (a concentration save, a
 * surprise save). The client shows a roller for the named character(s); the
 * submitted face(s) go back via the resume-submission action path.
 */
export const ROLL_REQUEST_PUSH_TYPE = "roll_request";
/**
 * Mirrors the `map_updated` push (lambda turn-worker tail + map-preauthor Lambda).
 * Signals that the campaign's spatial map changed — a mid-turn move/establish, a
 * hazard set/clear, or a background ring-ahead pre-author. The map is a SEPARATE
 * GET /map fetch that only re-ran on a `result` seqToken bump, so without this
 * cue a map change stayed stale in the browser until an unrelated event forced a
 * refetch ("the exits appeared on their own"). The client bumps a map-refresh
 * nonce on this push so the MapPanel refetches.
 */
export const MAP_UPDATED_PUSH_TYPE = "map_updated";

/** The base envelope every push carries — a `type` for dispatch (push-channel.ts PushMessage). */
export interface BasePush {
  readonly type: string;
  readonly [key: string]: unknown;
}

/** A free-flow shared-chat message any participant posted (R31.2). */
export interface FreeFlowMessagePush extends BasePush {
  readonly type: typeof FREE_FLOW_MESSAGE_PUSH_TYPE;
  readonly seq: number;
  readonly role: string;
  readonly authorId?: string | null;
  readonly text: string;
  readonly timestamp?: string;
  /**
   * Whether the line was spoken in character or out of character (OOC/IC axis).
   * Present only for an explicit `ooc` line; absent ⇒ the client treats it as
   * in character. Drives the journal speaker label (player name for OOC vs the
   * character name for IC).
   */
  readonly mode?: MessageMode;
}

/** A DM interjection pushed asynchronously into the shared channel. */
export interface DmInterjectionPush extends BasePush {
  readonly type: typeof DM_INTERJECTION_PUSH_TYPE;
  readonly text: string;
  readonly seq?: number;
  readonly timestamp?: string;
}

/**
 * A player-safe session recap (§3.5b) pushed when a returning session crosses
 * the away-gap boundary. A "previously, in your campaign…" journal line, built
 * server-side from player-visible state only (rolling summary, location, open
 * quests) — never DM-only arcs or secrets. Shown as a journal entry, not
 * transient UI.
 */
export interface SessionRecapPush extends BasePush {
  readonly type: typeof RECAP_PUSH_TYPE;
  readonly campaignId: string;
  readonly text: string;
}

/**
 * A committed SYSTEM/audit log entry fanned out live (the "Updated <char>: …" /
 * "Campaign updated: location -> …" notification). Mirrors the seq-keyed
 * free-flow push shape so the client folds it into a `system` journal entry
 * keyed `chat:${seq}` — the SAME id the hydration read uses — so a live push and
 * a later re-hydration MERGE in place instead of double-rendering. Previously
 * these were appended server-side but never pushed, so they only appeared after
 * a journal re-hydration (nav away + back).
 */
export interface SystemAuditPush extends BasePush {
  readonly type: typeof SYSTEM_AUDIT_PUSH_TYPE;
  readonly seq: number;
  readonly role: string;
  readonly authorId?: string | null;
  readonly text: string;
  readonly timestamp?: string;
}

/**
 * A request for a player to roll an owed die for a PAUSED turn (multi-leg turn
 * model A3). The turn blocked waiting on this human-controlled character's die
 * (e.g. a concentration save after taking damage); the client shows a roller and
 * sends the raw face(s) back via the resume-submission action path, keyed by the
 * owed `rollId`. `faceCount` is 1 normally / 2 at advantage-disadvantage.
 */
export interface RollRequestPush extends BasePush {
  readonly type: typeof ROLL_REQUEST_PUSH_TYPE;
  readonly campaignId: string;
  /** The paused turn's logical id (echoed back on submission). */
  readonly logicalTurnId: string;
  /** The owed roll(s) — a list so a party-wide save can request several at once. */
  readonly owed: readonly {
    /** The owed-roll id the submission is keyed by (PendingRollContext.id). */
    readonly rollId: string;
    /** Whose die is owed. */
    readonly characterId: string;
    /** The roll kind (save/check/concentration/opposed/death_save) for labeling. */
    readonly kind: string;
    /** The DC to beat, when the roll is vs a fixed DC (absent for an opposed roll). */
    readonly dc?: number;
    /** How many d20 faces to collect (1 normal, 2 at advantage/disadvantage). */
    readonly faceCount: number;
    /** A player-facing reason ("the ogre's blow rattles your focus"). */
    readonly reason: string;
  }[];
}

/** Toggles the "Rufus is writing…" indicator for a turn. */
export interface WritingIndicatorPush extends BasePush {
  readonly type: typeof WRITING_INDICATOR_PUSH_TYPE;
  readonly turnId: string;
  readonly writing: boolean;
}

/**
 * The in-character cold-start waiting cue (R15.2): while an Aurora cold start
 * resumes, the async worker fans out this push so the client shows "Rufus
 * consults the archives…" instead of a raw connection error. Transient UI state
 * (like the writing indicator), NOT a journal line — cleared by the next
 * result/signal. `message` carries the in-character text from the server (the
 * authoritative `RUFUS_CONSULTS_THE_ARCHIVES` constant, staleness.ts /
 * cold-start.ts).
 */
export interface ColdStartWaitingPush extends BasePush {
  readonly type: typeof COLD_START_WAITING_PUSH_TYPE;
  readonly turnId: string;
  readonly campaignId: string;
  readonly message: string;
}

/**
 * A signal that the campaign's spatial map changed (map-staleness fix). Carries
 * only the discriminator + campaignId — the client reacts by refetching the
 * separate GET /map, so no map payload rides the push. Fired from the turn-worker
 * tail (a committed map-changing turn) and from the off-pipeline map-preauthor
 * Lambda (a background ring-ahead pass that authored geometry).
 */
export interface MapUpdatedPush extends BasePush {
  readonly type: typeof MAP_UPDATED_PUSH_TYPE;
  readonly campaignId: string;
}

/** A committed, validated turn result (TurnResultPush, kind:"result"). */
export interface TurnResultPush extends BasePush {
  readonly type: "result";
  readonly kind: "result";
  readonly turnId: string;
  readonly campaignId: string;
  readonly narrative: string;
  readonly committedSeqToken?: number;
}

/** A clean retry signal on turn failure (TurnRetryPush, kind:"retry") — no raw error (R13.5). */
export interface TurnRetryPush extends BasePush {
  readonly type: "retry";
  readonly kind: "retry";
  readonly turnId: string;
  readonly campaignId: string;
  readonly reason: string;
}

/** The full discriminated union of pushes the play view handles. */
export type RufusPush =
  | FreeFlowMessagePush
  | DmInterjectionPush
  | SessionRecapPush
  | SystemAuditPush
  | RollRequestPush
  | WritingIndicatorPush
  | ColdStartWaitingPush
  | MapUpdatedPush
  | TurnResultPush
  | TurnRetryPush;

/**
 * Parse an incoming WebSocket message payload into a typed {@link RufusPush}.
 * Returns `null` for anything that is not a recognized, well-formed push so the
 * client can ignore noise without crashing. The push channel serializes the
 * envelope with `type` set to the message's discriminator (for turn pushes the
 * server maps `kind` -> `type`, so both are present).
 */
export function parsePush(raw: unknown): RufusPush | null {
  let value: unknown = raw;
  if (typeof raw === "string") {
    try {
      value = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (typeof value !== "object" || value === null) return null;
  const msg = value as Record<string, unknown>;
  const type = msg.type;
  if (typeof type !== "string") return null;

  switch (type) {
    case FREE_FLOW_MESSAGE_PUSH_TYPE:
      return typeof msg.text === "string" && typeof msg.seq === "number"
        ? (msg as unknown as FreeFlowMessagePush)
        : null;
    case DM_INTERJECTION_PUSH_TYPE:
      return typeof msg.text === "string"
        ? (msg as unknown as DmInterjectionPush)
        : null;
    case RECAP_PUSH_TYPE:
      return typeof msg.text === "string" && typeof msg.campaignId === "string"
        ? (msg as unknown as SessionRecapPush)
        : null;
    case SYSTEM_AUDIT_PUSH_TYPE:
      return typeof msg.text === "string" && typeof msg.seq === "number"
        ? (msg as unknown as SystemAuditPush)
        : null;
    case ROLL_REQUEST_PUSH_TYPE:
      return Array.isArray(msg.owed) && typeof msg.logicalTurnId === "string" && typeof msg.campaignId === "string"
        ? (msg as unknown as RollRequestPush)
        : null;
    case WRITING_INDICATOR_PUSH_TYPE:
      return typeof msg.writing === "boolean" && typeof msg.turnId === "string"
        ? (msg as unknown as WritingIndicatorPush)
        : null;
    case COLD_START_WAITING_PUSH_TYPE:
      return typeof msg.message === "string" && typeof msg.turnId === "string"
        ? (msg as unknown as ColdStartWaitingPush)
        : null;
    case MAP_UPDATED_PUSH_TYPE:
      return typeof msg.campaignId === "string"
        ? (msg as unknown as MapUpdatedPush)
        : null;
    case "result":
      return typeof msg.narrative === "string" && typeof msg.turnId === "string"
        ? (msg as unknown as TurnResultPush)
        : null;
    case "retry":
      return typeof msg.reason === "string" && typeof msg.turnId === "string"
        ? (msg as unknown as TurnRetryPush)
        : null;
    default:
      return null;
  }
}
